import React from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { AiAsset } from "./types/ai.types";


type Props = {
  assets: AiAsset[];
  selectedAssetId?: string;
  onSelect: (asset: AiAsset) => void;
  onApply?: (asset: AiAsset) => void;
};

export const AiImageVersionsGallery: React.FC<Props> = ({
  assets,
  selectedAssetId,
  onSelect,
  onApply,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const selectedBorder = useColorModeValue("teal.500", "teal.300");
  const normalBorder = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const emptyBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const imageBg = useColorModeValue("gray.100", "gray.700");

  if (!assets.length) {
    return (
      <Box
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={normalBorder}
        bg={emptyBg}
        borderRadius="2xl"
        p={8}
        textAlign="center"
      >
        <Text color={mutedText} fontSize="sm">
          No image versions yet. Upload an image to start editing.
        </Text>
      </Box>
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
      {assets.map((asset) => {
        const assetId = asset._id || asset.id || asset.url;
        const selected = selectedAssetId === assetId;

        return (
          <Box
            key={assetId}
            bg={cardBg}
            borderWidth="2px"
            borderColor={selected ? selectedBorder : normalBorder}
            borderRadius="2xl"
            overflow="hidden"
            cursor="pointer"
            boxShadow={selected ? "md" : "sm"}
            onClick={() => onSelect(asset)}
          >
            <Image
              src={asset.url}
              alt={asset.kind}
              w="100%"
              h="180px"
              objectFit="cover"
              bg={imageBg}
            />

            <VStack align="stretch" p={3} spacing={2}>
              <HStack justify="space-between" align="start">
                <Badge colorScheme={asset.kind === "original" ? "gray" : "teal"}>
                  {asset.kind}
                </Badge>

                {asset.provider && (
                  <Badge colorScheme={asset.provider === "openai" ? "purple" : "gray"}>
                    {asset.provider}
                  </Badge>
                )}
              </HStack>

              <Text fontSize="xs" color={mutedText} noOfLines={1}>
                {asset.createdAt
                  ? new Date(asset.createdAt).toLocaleString()
                  : "New asset"}
              </Text>

              <Button
                size="xs"
                colorScheme="teal"
                variant={selected ? "solid" : "outline"}
                onClick={(event) => {
                  event.stopPropagation();
                  onApply?.(asset);
                }}
              >
                Use this image
              </Button>
            </VStack>
          </Box>
        );
      })}
    </SimpleGrid>
  );
};